import React, { useState } from 'react';
import { FiX, FiSend, FiAlertCircle } from 'react-icons/fi';

/**
 * RequestSubmissionForm - Form for submitting introduction requests
 *
 * Alumni describe what they're seeking (not who specifically).
 * NACME's matching algorithm finds compatible targets.
 */

// Industry options for matching
const INDUSTRIES = [
  'Technology',
  'Financial Services',
  'Healthcare',
  'Manufacturing',
  'Semiconductor Manufacturing',
  'Chemicals',
  'Medical Manufacturing',
  'Beauty & Cosmetics',
  'Biotechnology',
  'Food Services',
  'Energy',
  'Aerospace & Defense',
  'Automotive',
  'Consumer Goods',
  'Telecommunications',
  'Consulting',
  'Government',
  'Education',
  'Construction / Real Estate',
  'Non-Profit',
  'Hospitality',
  'Agriculture',
  'Other',
];

// Function areas
const FUNCTIONS = [
  'Engineering',
  'Product Management',
  'Operations',
  'Finance',
  'Marketing',
  'Sales',
  'Human Resources',
  'Legal',
  'Research & Development',
  'Strategy',
  'General Management',
  'Entrepreneurship',
  'Other',
];

// Format preferences
const FORMATS = [
  { value: 'video_call', label: 'Video Call' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'email_intro', label: 'Email Introduction' },
];

// Request type labels - must match backend enum values
const REQUEST_TYPE_LABELS = {
  'PEER_MID_LEVEL': 'Peer Networking',
  'PEER_SENIOR': 'Senior Peer Networking',
  'MENTORSHIP_UP': 'Seek Mentorship',
  'MENTORSHIP_HIGH': 'Offer Mentorship',
  'BOARD_PATHWAY': 'Board Pathway',
  'HBCU_MENTORSHIP': 'HBCU Mentorship',
};

const RequestSubmissionForm = ({
  requestType,
  onClose,
  onSubmit,
  isSubmitting,
  maxRequests = 3,
  currentRequestCount = 0,
}) => {
  const [formData, setFormData] = useState({
    industries: [],
    functions: [],
    geographic_preference: '',
    goals: '',
    format_preference: 'video_call',
  });
  const [conductAgreed, setConductAgreed] = useState(false);
  const [errors, setErrors] = useState({});

  const canSubmit = currentRequestCount < maxRequests;

  const handleIndustryToggle = (industry) => {
    setFormData((prev) => ({
      ...prev,
      industries: prev.industries.includes(industry)
        ? prev.industries.filter((i) => i !== industry)
        : [...prev.industries, industry],
    }));
    if (errors.industries) {
      setErrors((prev) => ({ ...prev, industries: null }));
    }
  };

  const handleFunctionToggle = (func) => {
    setFormData((prev) => ({
      ...prev,
      functions: prev.functions.includes(func)
        ? prev.functions.filter((f) => f !== func)
        : [...prev.functions, func],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (formData.industries.length === 0) {
      newErrors.industries = 'Please select at least one industry preference';
    }

    if (formData.goals.trim().length < 50) {
      newErrors.goals = 'Please describe your goals in at least 50 characters';
    }

    if (!conductAgreed) {
      newErrors.conduct = 'You must agree to the standards of professional conduct to submit a request';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      request_type: requestType,
      request_payload: {
        // Map frontend field names to backend schema field names
        desired_industries: formData.industries,
        desired_expertise: formData.functions.length > 0 ? formData.functions : null,
        geographic_preferences: formData.geographic_preference ? [formData.geographic_preference] : null,
        request_context: formData.goals.trim(),
        additional_preferences: {
          format_preference: formData.format_preference,
        },
      },
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              Request: {REQUEST_TYPE_LABELS[requestType] || requestType}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Describe what you're seeking. Our team will find the right match.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {!canSubmit && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-3">
              <FiAlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-800 dark:text-amber-200 font-medium">
                  Maximum requests reached
                </p>
                <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
                  You have {currentRequestCount} active requests. Please wait for one to complete or withdraw before submitting a new request.
                </p>
              </div>
            </div>
          )}

          {/* Industry Preferences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Industry Preferences <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Select industries where you'd like to connect
            </p>
            <div className="flex flex-wrap gap-2">
              {INDUSTRIES.map((industry) => (
                <button
                  key={industry}
                  type="button"
                  onClick={() => handleIndustryToggle(industry)}
                  className={`
                    px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${formData.industries.includes(industry)
                      ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-300 dark:border-indigo-700'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {industry}
                </button>
              ))}
            </div>
            {errors.industries && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.industries}</p>
            )}
          </div>

          {/* Function Preferences */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Function/Role Preferences
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Optional: Select functional areas of interest
            </p>
            <div className="flex flex-wrap gap-2">
              {FUNCTIONS.map((func) => (
                <button
                  key={func}
                  type="button"
                  onClick={() => handleFunctionToggle(func)}
                  className={`
                    px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                    ${formData.functions.includes(func)
                      ? 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 border-2 border-purple-300 dark:border-purple-700'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                >
                  {func}
                </button>
              ))}
            </div>
          </div>

          {/* Geographic Preference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Geographic Preference
            </label>
            <input
              type="text"
              name="geographic_preference"
              value={formData.geographic_preference}
              onChange={handleChange}
              placeholder="e.g., New York, Bay Area, or 'No preference'"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent"
            />
          </div>

          {/* Goals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {requestType === 'MENTORSHIP_HIGH'
                ? 'What do you hope to share or achieve with mentees?'
                : 'What do you hope to learn or achieve?'} <span className="text-red-500">*</span>
            </label>
            <textarea
              name="goals"
              value={formData.goals}
              onChange={handleChange}
              rows={4}
              placeholder={requestType === 'MENTORSHIP_HIGH'
                ? "Describe what you'd like to share with mentees. What expertise or experiences can you offer? What topics are you passionate about guiding others on?"
                : "Describe your goals for this introduction. What topics would you like to discuss? What guidance are you seeking?"}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:border-transparent resize-none"
            />
            <p className={`text-xs mt-1 ${formData.goals.length < 50 ? 'text-gray-500 dark:text-gray-400' : 'text-green-600 dark:text-green-400'}`}>
              {formData.goals.length}/50 characters minimum
            </p>
            {errors.goals && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.goals}</p>
            )}
          </div>

          {/* Format Preference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Preferred Format
            </label>
            <div className="flex gap-4">
              {FORMATS.map((format) => (
                <label key={format.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="format_preference"
                    value={format.value}
                    checked={formData.format_preference === format.value}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{format.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Professional Conduct Agreement */}
          <div className="bg-gray-50 dark:bg-gray-900/40 border border-gray-300 dark:border-gray-600 rounded-lg p-4">
            <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-2">
              Professional Conduct Agreement
            </p>
            <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed">
              By submitting this request, you agree to abide by{' '}
              <a
                href="/professional-conduct"
                target="_blank"
                rel="noopener noreferrer"
                className="text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300"
              >
                these standards of professional conduct
              </a>{' '}
              and assert that this inquiry is career-related. You agree that any introduction
              facilitated through the NACME Executive Relationship Program will be governed by
              these standards and that the discovery of inappropriate or unprofessional conduct
              will result in removal from the program.
            </p>
            <label className="flex items-start gap-3 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={conductAgreed}
                onChange={(e) => {
                  setConductAgreed(e.target.checked);
                  if (errors.conduct) {
                    setErrors((prev) => ({ ...prev, conduct: null }));
                  }
                }}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-xs text-gray-800 dark:text-gray-200">
                I have read and agree to the{' '}
                <a
                  href="/professional-conduct"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-300"
                >
                  standards of professional conduct
                </a>{' '}
                and assert that this introduction request is for career development purposes.
              </span>
            </label>
            {errors.conduct && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-2">{errors.conduct}</p>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 rounded-lg transition-colors"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FiSend className="w-4 h-4" />
                  Submit Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestSubmissionForm;
